import React, { useCallback } from 'react';
import proptypes from 'prop-types';
const { func, objectOf } = proptypes;
import _noop from 'lodash/noop';
import _indexOf from 'lodash/indexOf';
import _map from 'lodash/map';
import _size from 'lodash/size';
import {
  Tile, TileText, TileContent, Button, useBreakpoint, Badge
} from '@one-thd/sui-atomic-components';
import {
  useDropZoneStore, useDigitalDataStore
} from '../../store';
import { useDesignStore } from '../../store/design/context';
import { Draggable } from '../../components/Draggable';
import logNewRelicCustom from '../../utils/logNewRelicCustom';
import useBundleDrawer from '../../hooks/useBundleDrawer';
import {
  bundleDataShape, choiceMetadataShape, choiceShape, metaDataItemShape, productDataShape
} from '../types';
import ChoiceBundlePrice from './components/ChoiceBundlePrice';
import ChoiceBundleQuantity from './components/ChoiceBundleQuantity';
import ChoiceImage from './components/ChoiceImage';
import { ACTIVE_GHOST_BTN_OVERRIDE, INACTIVE_GHOST_BTN_OVERRIDE } from './_styles';
import { NEW_RELIC_EVENTS } from '../../constants';

const Bundle = ({
  choice: {
    id,
    mediaUrl,
    shortLabel,
    sku,
    inExcludedShipState,
    promoType,
    disabled,
    isAvailable
  },
  quickViewHandler,
  choiceMetaData,
  metadata,
  bundle,
  product
}) => {
  const selectProduct = useDesignStore((state) => state.select);
  const resetDropZones = useDropZoneStore((state) => state.resetDropZones);
  const trackQuickViewModalLoadEvent = useDigitalDataStore((state) => state.trackQuickViewModalLoadEvent);
  const selectedProduct = useDesignStore((state) => state.design.selectedProduct);
  const { setBundle } = useBundleDrawer();

  const matches = useBreakpoint('lg');
  const isDesktop = matches.greater;

  const handleProductClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    selectProduct({ metadata: choiceMetaData, choiceId: id }, resetDropZones);
    logNewRelicCustom(NEW_RELIC_EVENTS.CLICK_START_BUNDLE_CHOICE, { choiceId: id });
  }, []);

  const onClickQuickView = (itemId) => {
    trackQuickViewModalLoadEvent();
    quickViewHandler({ itemId });
  };

  const handleQuickViewClick = (itemId) => {
    if (bundle?.id) {
      setBundle(bundle?.id);
    } else {
      onClickQuickView(itemId);
    }

    logNewRelicCustom(NEW_RELIC_EVENTS.BUNDLE_QUICK_VIEW, { itemId });
  };

  const getStyle = (style) => {
    return isDesktop ? style.desktop : style.mobile;
  };

  const tileStyle = {
    desktop: {
      height: 250,
      // width: 150,
      alignItems: 'flex-start',
      border: `2px solid ${id === selectedProduct?.choiceId ? '#f96302' : '#ccc'}`,
      borderRadius: '3px 3px 0px 0px'
    },
    mobile: {
      height: 205,
      // width: 120,
      alignItems: 'flex-start',
      border: `2px solid ${id === selectedProduct?.choiceId ? '#f96302' : '#ccc'}`,
      borderRadius: '3px 3px 0px 0px'
    }
  };

  const responsiveTileStyle = getStyle(tileStyle);

  const buttonStyle = {
    desktop: {
      color: !product ? '#ccc' : '#3E7697',
      border: '2px solid #ccc',
      borderTop: 'none',
      height: 44,
      width: '100%',
      borderRadius: '0px 0px 3px 3px'
    },
    mobile: {
      color: '#3E7697',
      border: '2px solid #ccc',
      borderTop: 'none',
      height: 30,
      width: '100%',
      borderRadius: '0px 0px 3px 3px'
    }
  };

  const responsiveButtonStyle = getStyle(buttonStyle);

  const imageStyle = {
    desktop: {
      height: 100,
      width: 100,
    },
    mobile: {
      height: 65,
      width: 65,
    }
  };

  const responsiveImageStyle = getStyle(imageStyle);

  return id && (
    <>
      <div
        className="sui-flex sui-flex-col choice"
        role="button"
        tabIndex={0}
        data-choice-id={id}
        data-sku={bundle}
      >
        <Tile
          component="div"
          value={sku}
          style={responsiveTileStyle}
          onMouseDown={(event) => handleProductClick(event)}
          onTouchStart={(event) => handleProductClick(event)}
          aria-label={`choice ${shortLabel}`}
          disabled={disabled}
          selected={false}
        >
          <TileContent
            style={{ alignSelf: 'stretch', justifyContent: 'space-between', width: '100%' }}
            orientation="vertical"
            alignItems="start"
          >
            <div
              style={{ height: responsiveImageStyle.height }}
              className="sui-flex sui-items-center sui-flex-col sui-self-center"
            >
              {product?.fulfillment && (
                <>
                  {(!isAvailable && !inExcludedShipState) && (
                    <span className="sui-absolute custom-badge">
                      <Badge size="small" variant="regular" color="danger">Out of Stock</Badge>
                    </span>
                  )}
                  {(!isAvailable && inExcludedShipState) && (
                    <span className="sui-absolute custom-badge">
                      <Badge size="small" variant="regular" color="danger">Unavailable</Badge>
                    </span>
                  )}
                </>
              )}
              <Draggable isVisualizerDrag={false} id={id} choiceId={id}>
                <ChoiceImage
                  disabled={disabled}
                  mediaUrl={mediaUrl}
                />
              </Draggable>
            </div>
            <TileText align="left" lineClamp="3" height="normal" style={{ maxHeight: '6rem' }}>{shortLabel}</TileText>
            <ChoiceBundlePrice skus={bundle?.bundleSkus} />
            <ChoiceBundleQuantity choiceId={id} bundleSize={_size(bundle?.bundleChoices)} />
          </TileContent>
        </Tile>
        <Button
          style={responsiveButtonStyle}
          className={!product ? INACTIVE_GHOST_BTN_OVERRIDE : ACTIVE_GHOST_BTN_OVERRIDE}
          onClick={
            !product
              ? () => null
              : () => handleQuickViewClick(sku)
          }
          variant="ghost"
          value={id}
          disabled={!product}
        >
          Quick View
        </Button>
      </div>
    </>
  );
};

Bundle.propTypes = {
  choice: choiceShape.isRequired,
  quickViewHandler: func,
  choiceMetaData: choiceMetadataShape.isRequired,
  product: productDataShape,
  bundle: bundleDataShape,
  metadata: objectOf(metaDataItemShape).isRequired
};

Bundle.defaultProps = {
  quickViewHandler: _noop,
  product: null,
  bundle: {}
};

export default Bundle;
